import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  EnvironmentOutlined,
  UserOutlined,
  IdcardOutlined
} from '@ant-design/icons'
import { Space } from 'antd'
import Spin from 'components/SpinA11y'
import debounce from 'lodash.debounce'

import { PageContainer } from 'ui/styled'

import Header from 'components/Header'
import MemberSelect from 'components/MemberSelect'
import FilterSystem from 'components/FilterSystem'
import ProviderNameSearch from 'components/ProviderNameSearch'
import ProvidersFilters from 'components/ProvidersFilters'

import CarePlanDraftSider from 'pages/CarePlanDraft/components/Sider'
import { useCarePlanDraftStore } from 'stores/carePlanDraft'

import LocationsAutocomplete from 'components/LocationsAutocomplete'
import InsuranceAutocomplete from 'components/InsuranceAutocomplete'
import Map from 'components/Map'
import { PROVIDERS_PATH } from 'constants/routes'

import {
  getFromStorage,
  removeFromStorage,
  setToStorage
} from 'utils/localStorage'
import { isNilOrEmpty } from 'utils/helpers'

import {
  Root,
  SearchSection,
  ResultsSection,
  ResultsTable,
  ResultsMap,
  IconContainer
} from 'ui/entities/styled'

import ProvidersTable from './ProvidersTable'

const FILTERS_STORAGE_NAME = 'filters_providers_page'

const DEFAULT_FILTERS = {
  distance: 20,
  min_confidence: null
}

const Providers = ({
  onSelectEntity = null,
  onRemoveEntity = null,
  entitiesIDs = [],
  isStandalonePage = false,
  memberId = false,
  isCarePlan,
  careStepFilters,
  resetCareStepFilters,
  requestType
}) => {
  const { carePlanDraft } = useFlags()
  const [searchParams] = useSearchParams()

  const [filtersResetKey, setFiltersResetKey] = useState(Date.now())
  const [markers, setMarkers] = useState()
  const [markersLoading, setMarkersLoading] = useState(true)
  const [planTiers, setPlanTiers] = useState()

  const activeDraftId = useCarePlanDraftStore(s => s.activeDraftId)
  const isCarePlanDraftCreation =
    carePlanDraft && isStandalonePage && activeDraftId

  const getInitialFilters = () => {
    if (careStepFilters) return careStepFilters
    if (!isStandalonePage) return DEFAULT_FILTERS

    if (searchParams.size) {
      const initialFilters = {
        ...DEFAULT_FILTERS,
        ...Object.fromEntries(searchParams)
      }

      setToStorage(FILTERS_STORAGE_NAME)(initialFilters)
      return initialFilters
    }

    return getFromStorage(FILTERS_STORAGE_NAME)() || DEFAULT_FILTERS
  }

  const filtersSaved = getInitialFilters()

  const [insuranceModalState, setInsuranceModalState] = useState({
    visible: false
  })

  const handleEntitySelect = useCallback(
    entity => {
      if (onSelectEntity) return onSelectEntity(entity)
      if (isCarePlanDraftCreation) {
        return setInsuranceModalState({
          visible: true,
          data: {
            entity,
            id: entity.id,
            name: entity.full_name,
            avatarSrc: entity.image_url,
            hideModal: () => setInsuranceModalState({ visible: false })
          }
        })
      }
    },
    [isCarePlanDraftCreation, onSelectEntity]
  )

  const filterSystemRenderKey = isNilOrEmpty(activeDraftId)
    ? 'filter-system'
    : `filter-system-${activeDraftId}`

  return (
    <div>
      {isStandalonePage && (
        <>
          <Helmet>
            <title>Providers | Rightway Healthcare</title>
          </Helmet>
          <Header title="Providers" isDraftsMenuVisible isArrowHidden />
        </>
      )}
      <FilterSystem key={filterSystemRenderKey} defaults={filtersSaved}>
        {(filterValues, updateFilters, updateSeveralFilters, resetFilters) => {
          const filtersReset = () => {
            if (isCarePlan) {
              resetCareStepFilters()
            }
            if (isStandalonePage || isCarePlan)
              removeFromStorage(FILTERS_STORAGE_NAME)
            resetFilters(DEFAULT_FILTERS)
          }

          const filtersUpdateSeveral = val => {
            const res = updateSeveralFilters(val)
            if (isStandalonePage) setToStorage(FILTERS_STORAGE_NAME)(res)
          }

          const filtersUpdate = (name, value) => {
            const res = updateFilters(name, value)
            if (isStandalonePage) setToStorage(FILTERS_STORAGE_NAME)(res)
          }

          const onResetFilters = () => {
            setFiltersResetKey(Date.now())
            filtersReset()
          }

          const onAddressSelect = v => {
            filtersUpdate('address', v)
          }

          const debouncedFiltersUpdate = debounce(filtersUpdate, 500)
          const debouncedAddressSelect = debounce(onAddressSelect, 500)

          return (
            <Root>
              <PageContainer
                key={filtersResetKey}
                width="100%"
                padding="20px"
                margin={!isStandalonePage && '0'}
                style={
                  !isStandalonePage ? { height: 'calc(100vh - 340px)' } : {}
                }
              >
                <SearchSection>
                  <Space
                    orientation="vertical"
                    size="middle"
                    style={{ width: '100%' }}
                  >
                    <Space.Compact size="large" block>
                      <ProviderNameSearch
                        filterValues={filterValues}
                        filtersUpdateSeveral={filtersUpdateSeveral}
                        onChange={debouncedFiltersUpdate}
                      />

                      <IconContainer>
                        <EnvironmentOutlined />
                      </IconContainer>
                      <LocationsAutocomplete
                        memberId={
                          isStandalonePage ? filterValues.member_id : memberId
                        }
                        onSelect={onAddressSelect}
                        onSearch={debouncedAddressSelect}
                        defaultValue={filterValues.address}
                      />
                      {isStandalonePage && (
                        <>
                          <IconContainer>
                            <UserOutlined />
                          </IconContainer>
                          <MemberSelect
                            allowClear
                            onChange={value => {
                              filtersUpdateSeveral([
                                { member_id: value },
                                { plan_id: undefined },
                                { insurance_plan_tiers: undefined }
                              ])
                              setPlanTiers(null)
                            }}
                            value={filterValues.member_id}
                            style={{ minWidth: '15%' }}
                            fontSize={16}
                          />
                        </>
                      )}

                      <IconContainer>
                        <IdcardOutlined />
                      </IconContainer>
                      <InsuranceAutocomplete
                        memberId={
                          isStandalonePage ? filterValues.member_id : memberId
                        }
                        onChange={value => {
                          filtersUpdateSeveral([
                            { plan_id: value },
                            { insurance_plan_tiers: undefined }
                          ])
                          setPlanTiers(null)
                        }}
                        onPlanSelect={setPlanTiers}
                        defaultValue={filterValues.plan_id}
                        isCarePlan={isCarePlan}
                      />
                    </Space.Compact>
                    <ProvidersFilters
                      filterValues={filterValues}
                      planTiers={planTiers}
                      filtersUpdate={filtersUpdate}
                      filtersUpdateSeveral={filtersUpdateSeveral}
                      onResetFilters={onResetFilters}
                      requestType={requestType}
                    />
                  </Space>
                </SearchSection>
                <ResultsSection>
                  <ResultsTable>
                    <ProvidersTable
                      filterValues={filterValues}
                      setMarkers={setMarkers}
                      setMarkersLoading={setMarkersLoading}
                      entitiesIDs={entitiesIDs}
                      onSelectEntity={handleEntitySelect}
                      onRemoveEntity={onRemoveEntity}
                      isCarePlan={isCarePlan}
                      isStandalonePage={isStandalonePage}
                      isSelectionMode={isCarePlan || isCarePlanDraftCreation}
                      employee_id={
                        isStandalonePage ? filterValues.member_id : memberId
                      }
                    />
                  </ResultsTable>

                  {!isCarePlanDraftCreation && (
                    <ResultsMap data-test="providersLocationsMap">
                      {markersLoading ? (
                        <Spin name="providersMap" spinning />
                      ) : (
                        <Map
                          markers={markers}
                          leftClickOpens={PROVIDERS_PATH}
                          rightClickOpens={false}
                        />
                      )}
                    </ResultsMap>
                  )}
                </ResultsSection>
              </PageContainer>

              {isCarePlanDraftCreation && (
                <CarePlanDraftSider insuranceModalState={insuranceModalState} />
              )}
            </Root>
          )
        }}
      </FilterSystem>
    </div>
  )
}

Providers.propTypes = {
  onSelectEntity: PropTypes.func,
  onRemoveEntity: PropTypes.func,
  entitiesIDs: PropTypes.array,
  isStandalonePage: PropTypes.bool,
  memberId: PropTypes.string
}

export default Providers
